import { Menu, SxProps, Typography } from '@mui/material';
import { MenuItem as MUIMenuItem } from '@mui/material';
import { noop } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';

import { MenuItem } from './MenuItem';

// Gap between the parent menu and the submenu. Kept in sync with the margin
// applied to the submenu paper below, since the gap consumes horizontal space
// when deciding which side the submenu fits on.
const SUBMENU_GAP_PX = 8;
// Move the submenu above its row anchor so the parent and child papers align.
const SUBMENU_VERTICAL_OFFSET_PX = 4;
// Minimum space to keep between the submenu and the viewport edge. Matches MUI
// Popover's default `marginThreshold` (16px): if the submenu would land within
// that distance of the edge, MUI nudges it back over the parent menu — the very
// overlap we're avoiding — so we must flip to the other side before that.
const VIEWPORT_EDGE_MARGIN_PX = 16;
const HOVER_GRACE_MS = 1000;
const SAFE_OVERFLOW_PX = 32;

export interface FlyoutMenuItemProps {
  icon?: React.ReactNode;
  endAdornment?: React.ReactNode;
  label: React.ReactNode;
  children?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  sx?: SxProps;
  typographyProps?: React.ComponentProps<typeof Typography>;
  muiMenuItemProps?: React.ComponentProps<typeof MUIMenuItem>;
}

/**
 * A menu item component that displays a submenu when hovered over.
 *
 * This component extends the basic MenuItem to add flyout/nested menu functionality.
 * When a user hovers over the menu item, it displays a submenu to the right of the
 * parent menu item. The submenu remains open for a short delay after the mouse leaves
 * to prevent accidental closing during normal mouse movement.
 *
 * When there isn't enough room to the right of the parent menu (e.g. the menu is
 * near the right edge of the screen), the submenu flips to open on the left
 * instead. MUI's Popover only nudges an overflowing submenu back on-screen, which
 * makes it overlap the parent menu — so we pick the side ourselves. The same
 * applies vertically: we measure space above and below the anchor and open
 * upward only when there's more usable room there than below.
 */

export function FlyoutMenuItem({
  icon,
  endAdornment,
  label,
  children,
  disabled,
  loading,
  sx,
  typographyProps,
  muiMenuItemProps,
}: FlyoutMenuItemProps) {
  const menuItemRef = useRef<HTMLLIElement>(null);
  // Stored as state (not just a ref) so the focus-on-open useEffect can
  // re-run once the Paper actually mounts. Refs don't trigger re-renders,
  // so a missed first attempt would otherwise never get a retry.
  const [paperEl, setPaperEl] = useState<HTMLDivElement | null>(null);

  // Whether the submenu opens to the left of its parent. We default to the
  // right and flip to the left when the submenu would overflow the viewport.
  const [openLeft, setOpenLeft] = useState(false);
  // Whether the submenu opens above its parent. Defaults to opening downward
  // and flips when there's more usable space above than below the anchor.
  // Same reason as the horizontal flip: MUI's Popover nudges instead of
  // flipping, which makes the submenu's top get clipped or land off-anchor.
  const [openAbove, setOpenAbove] = useState(false);
  // Tracks whether the submenu was opened via keyboard (ArrowRight on the
  // parent). Kept separate from the mouse-driven open state so the submenu
  // stays open while the user navigates inside it, even if the mouse never
  // entered the parent.
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  // When true, the next render that exposes the submenu should focus its
  // first menuitem. Cleared once handled.
  const [shouldFocusFirstOnOpen, setShouldFocusFirstOnOpen] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [safePolygon, setSafePolygon] = useState<{
    clipPath: string;
    anchorRect: DOMRect;
  } | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearCloseTimer = () => {
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
  };
  const setIsMouseInTargetArea = (inside: boolean, delay = 100) => {
    clearCloseTimer();
    setSafePolygon(null);
    if (inside) {
      setIsOpen(true);
    } else {
      closeTimer.current = setTimeout(() => {
        setIsOpen(false);
        setSafePolygon(null);
      }, delay);
    }
  };
  useEffect(() => () => clearCloseTimer(), []);
  const submenuOpen = isOpen || keyboardOpen;

  // Callback ref on the submenu paper: fires when the paper mounts (and again
  // with null on unmount). Measuring here — rather than in a layout effect keyed
  // on `isOpen` — guarantees the paper exists, since MUI mounts it on its own
  // schedule. The state update flushes before paint, so there's no visible jump.
  const measureSubmenuPosition = useCallback((paper: HTMLDivElement | null) => {
    setPaperEl(paper);

    // The paper unmounts when the submenu closes; reset to the defaults so
    // the next open starts fresh instead of reusing a stale choice measured
    // against the anchor's previous position.
    if (!paper) {
      setOpenLeft(false);
      setOpenAbove(false);
      return;
    }

    const anchor = menuItemRef.current;
    if (!anchor) {
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();

    // Horizontal: submenu occupies the gap plus its own width, and must
    // clear MUI's viewport margin so MUI doesn't nudge it back over the
    // parent menu.
    const spaceToRight = window.innerWidth - anchorRect.right;
    const spaceNeededOnRight =
      SUBMENU_GAP_PX + paper.offsetWidth + VIEWPORT_EDGE_MARGIN_PX;
    setOpenLeft(spaceToRight < spaceNeededOnRight);

    // Vertical: by default the submenu's top aligns with the anchor's top and
    // it extends downward; when flipped, its bottom aligns with the anchor's
    // bottom and it extends upward. Measure both directions so a submenu that
    // doesn't fit below isn't blindly flipped into an even tighter slot above
    // (which would clip entries off the top of the viewport).
    const spaceBelow = window.innerHeight - anchorRect.top;
    const spaceAbove = anchorRect.bottom;
    const spaceNeededVertically = paper.offsetHeight + VIEWPORT_EDGE_MARGIN_PX;
    const fitsBelow = spaceBelow >= spaceNeededVertically;
    const fitsAbove = spaceAbove >= spaceNeededVertically;
    // Prefer opening below when it fits (the natural reading direction). If
    // only one side fits, pick that one. If neither side fits, pick whichever
    // has more room and rely on the paper's `maxHeight` + internal scroll to
    // keep the content on screen.
    if (fitsBelow) {
      setOpenAbove(false);
    } else if (fitsAbove) {
      setOpenAbove(true);
    } else {
      setOpenAbove(spaceAbove > spaceBelow);
    }
  }, []);

  // Focus the first submenu item after it mounts when opened via keyboard.
  // Re-runs whenever `paperEl` becomes available, so a Paper that mounts
  // after the initial open (e.g. behind MUI's transition) still gets a
  // focus attempt rather than the flag being cleared on the first dry run.
  useEffect(() => {
    if (!submenuOpen || !shouldFocusFirstOnOpen || !paperEl) return;
    const item = paperEl.querySelector<HTMLElement>(
      '[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"]'
    );
    if (item) {
      item.focus();
      setShouldFocusFirstOnOpen(false);
    }
  }, [submenuOpen, shouldFocusFirstOnOpen, paperEl]);

  // If the submenu closes before the focus useEffect above had a chance to
  // run (ArrowLeft, onClose, parent blur), the intent flag would otherwise
  // leak into the next open — including a subsequent mouse hover, where it
  // would yank focus off whatever item the cursor lands on.
  useEffect(() => {
    if (!submenuOpen) setShouldFocusFirstOnOpen(false);
  }, [submenuOpen]);

  // --- Parent menuitem event handlers ---
  //
  // mouseenter opens the submenu and pulls
  // focus onto this row so a keyboard handoff continues from where the
  // mouse was. Two skip cases:
  //   • submenu already open — keep focus inside it instead of yanking it
  //     out the moment the mouse drifts onto the parent row;
  //   • an input is focused — don't steal keystrokes from e.g. a search
  //     input above the menu.
  const handleParentMouseEnter = (e: React.MouseEvent<HTMLLIElement>) => {
    setIsMouseInTargetArea(true);
    if (!submenuOpen && !(document.activeElement instanceof HTMLInputElement)) {
      menuItemRef.current?.focus();
    }
    muiMenuItemProps?.onMouseEnter?.(e);
  };

  const handleParentMouseMove = (e: React.MouseEvent<HTMLLIElement>) => {
    const paper = paperEl?.getBoundingClientRect();
    const anchorRect = menuItemRef.current?.getBoundingClientRect();
    if (paper && anchorRect) {
      // Build the path before leaving the row. Otherwise the sibling receives
      // mouseenter in the same event that starts protection and opens first.
      const edge = openLeft ? paper.right : paper.left;
      setSafePolygon({
        clipPath: `polygon(${e.clientX}px ${e.clientY}px, ${edge}px ${paper.top}px, ${edge}px ${paper.bottom}px)`,
        anchorRect,
      });
    }
    muiMenuItemProps?.onMouseMove?.(e);
  };

  // Entering the safe path cancels this timer. Leaving in another direction
  // closes promptly so a new category does not overlap the previous submenu.
  const handleParentMouseLeave = (e: React.MouseEvent<HTMLLIElement>) => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      setIsOpen(false);
      setSafePolygon(null);
    }, 100);
    muiMenuItemProps?.onMouseLeave?.(e);
  };

  // ArrowRight opens the submenu via keyboard and asks the focus effect to
  // move into the first submenu item once the Paper mounts. Other keys
  // fall through to whatever the consumer registered.
  const handleParentKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (children && e.key === 'ArrowRight') {
      e.preventDefault();
      e.stopPropagation();
      setKeyboardOpen(true);
      setShouldFocusFirstOnOpen(true);
      return;
    }
    muiMenuItemProps?.onKeyDown?.(e);
  };

  // When the parent loses focus to anywhere outside the submenu (e.g. a
  // sibling FlyoutMenuItem that MUI focuses on hover), drop keyboardOpen
  // so this submenu doesn't pin itself open behind the new one.
  const handleParentBlur = (e: React.FocusEvent<HTMLLIElement>) => {
    if (!paperEl?.contains(e.relatedTarget as Node | null)) {
      setKeyboardOpen(false);
    }
    muiMenuItemProps?.onBlur?.(e);
  };

  // Defensive: if the parent regains focus while the keyboard-opened
  // submenu is still flagged open, something pulled focus back — most
  // commonly an enforceFocus trap on the parent's Menu (which is the
  // default for consumers that don't pass `disableEnforceFocus`). Close
  // the submenu so it doesn't stay visible in an unreachable state.
  const handleParentFocus = (e: React.FocusEvent<HTMLLIElement>) => {
    if (keyboardOpen) setKeyboardOpen(false);
    muiMenuItemProps?.onFocus?.(e);
  };

  // --- Submenu Menu event handlers ---

  // Focus inside the submenu moved. Keep the submenu open if focus stays
  // inside the submenu or hops back to the parent (ArrowLeft / parent
  // hover-refocus); otherwise drop keyboardOpen.
  //
  // LIMITATION: this assumes the submenu's children are leaf MenuItems,
  // not nested FlyoutMenuItems. A nested flyout renders into its own
  // portal, so focus moving into it would be treated as leaving this
  // submenu and prematurely close this level. Today none of
  // FlyoutMenuItem's consumers nest; if you need to, widen this check to
  // recognize descendant flyout papers as "still inside".
  const handleSubmenuBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (!paperEl?.contains(next) && next !== menuItemRef.current) {
      setKeyboardOpen(false);
    }
  };

  // ArrowLeft closes the keyboard-opened submenu and returns focus to
  // the parent. The mouse-driven half of submenuOpen is left alone so a
  // still-hovering mouse doesn't suddenly close the menu out from under
  // itself. Arrow-nav events otherwise stop here so the outer MenuList
  // doesn't also advance focus and double up movement.
  const handleSubmenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      e.stopPropagation();
      setKeyboardOpen(false);
      menuItemRef.current?.focus();
      return;
    }
    if (
      e.key === 'ArrowDown' ||
      e.key === 'ArrowUp' ||
      e.key === 'Home' ||
      e.key === 'End'
    ) {
      e.stopPropagation();
    }
  };

  const handleSubmenuClose = () => {
    setKeyboardOpen(false);
    setIsMouseInTargetArea(false);
  };

  return (
    <>
      <MenuItem
        sx={sx}
        icon={icon}
        label={label}
        onClick={noop} // No-op since this is just a flyout parent
        disabled={disabled}
        loading={loading}
        typographyProps={typographyProps}
        muiMenuItemProps={{
          ...muiMenuItemProps,
          'aria-haspopup': children ? 'menu' : undefined,
          onMouseEnter: handleParentMouseEnter,
          onMouseLeave: handleParentMouseLeave,
          onMouseMove: handleParentMouseMove,
          onKeyDown: handleParentKeyDown,
          onBlur: handleParentBlur,
          onFocus: handleParentFocus,
          ref: menuItemRef,
        }}
        endAdornment={endAdornment}
        iconAfter={
          children &&
          !loading && <ChevronRightIcon size={16} color={COLORS.TEXT.PRIMARY} />
        }
      />
      {safePolygon &&
        createPortal(
          <div
            aria-hidden="true"
            onMouseEnter={clearCloseTimer}
            onMouseLeave={() => setIsMouseInTargetArea(false)}
            style={{
              position: 'fixed',
              inset: 0,
              clipPath: safePolygon.clipPath,
              zIndex: zIndexes.POPOVER + 1,
              pointerEvents: 'none',
              display: 'grid',
              gridTemplateRows: `${safePolygon.anchorRect.top}px ${safePolygon.anchorRect.height}px 1fr`,
            }}
          >
            {/* Only the regions outside the active row intercept the pointer.
                Covering the row would remove its native hover highlight. */}
            <div style={{ pointerEvents: 'auto' }} />
            <div
              style={{
                pointerEvents: 'auto',
                marginLeft: openLeft ? 0 : safePolygon.anchorRect.right,
                marginRight: openLeft
                  ? `calc(100% - ${safePolygon.anchorRect.left}px)`
                  : 0,
              }}
            />
            <div style={{ pointerEvents: 'auto' }} />
          </div>,
          document.body
        )}
      {children && (
        <Menu
          open={submenuOpen}
          // The parent Menu's FocusTrap would otherwise pull keyboard focus
          // back as soon as it lands in this portaled submenu. Disabling it
          // on the submenu (and on the parent — see AddBundleMenu) lets
          // focus flow freely between the two menus on ArrowRight/Left.
          disableEnforceFocus
          // Submenu's first menuitem on open is set explicitly via the
          // useEffect above. `autoFocus` controls MenuList's auto-focus
          // item; `disableAutoFocus` stops the underlying Modal from
          // focusing the Paper itself — without it, the Paper (which has
          // a scrollable maxHeight) takes focus and arrow keys scroll the
          // submenu instead of navigating between items.
          autoFocus={false}
          disableAutoFocus
          // Open instantly rather than fading in over MUI's ~250ms default.
          // The delay caused stale paper geometry to overlap freshly-opened
          // siblings and doesn't match the snappy submenu on the
          // households list page.
          transitionDuration={0}
          onMouseEnter={() => setIsMouseInTargetArea(true)}
          onMouseLeave={(e) => {
            const rect = paperEl?.getBoundingClientRect();
            const inOverflow =
              rect &&
              e.clientY >= rect.top &&
              e.clientY <= rect.bottom &&
              (openLeft
                ? e.clientX <= rect.left &&
                  e.clientX >= rect.left - SAFE_OVERFLOW_PX
                : e.clientX >= rect.right &&
                  e.clientX <= rect.right + SAFE_OVERFLOW_PX);
            setIsMouseInTargetArea(false, inOverflow ? HOVER_GRACE_MS : 100);
          }}
          onBlur={handleSubmenuBlur}
          onKeyDown={handleSubmenuKeyDown}
          onClose={handleSubmenuClose}
          anchorEl={menuItemRef.current}
          slotProps={{ paper: { ref: measureSubmenuPosition } }}
          anchorOrigin={{
            vertical: openAbove ? 'bottom' : 'top',
            horizontal: openLeft ? 'left' : 'right',
          }}
          transformOrigin={{
            vertical: openAbove ? 'bottom' : 'top',
            horizontal: openLeft ? 'right' : 'left',
          }}
          sx={{
            // Match the POPOVER layer so the submenu renders above SideDrawers
            // (zIndex.drawer), which otherwise cover MUI menus' default
            // zIndex.modal and make the submenu unclickable.
            zIndex: zIndexes.POPOVER,
            pointerEvents: 'none',
            '& .MuiPaper-root': {
              pointerEvents: 'auto',
              // Cap the paper itself (not the popper container) so a tall
              // list scrolls internally instead of being clipped by MUI's
              // viewport-edge nudging.
              maxHeight: `calc(100dvh - ${VIEWPORT_EDGE_MARGIN_PX * 2}px)`,
              overflowY: 'auto',
              // Flyouts align to the parent row, not below a trigger button.
              // Replace the global Popover offset used by standard dropdowns.
              marginTop: `${-SUBMENU_VERTICAL_OFFSET_PX}px`,
              // Keep a small gap between the parent menu and the submenu on
              // whichever side it opens.
              ...(openLeft
                ? { marginLeft: `${-SUBMENU_GAP_PX}px` }
                : { marginLeft: `${SUBMENU_GAP_PX}px` }),
            },
          }}
        >
          {children}
        </Menu>
      )}
    </>
  );
}
